import ApiService from '@/services/api'
import { saveBoard, saveStyle, saveOption, saveDetail } from '@/store/utils'

/**
 * @param   {Store}         context
 * @param   {Store.commit}  context.commit
 * @param   {Store.getters} context.getters
 * @param   {object}        payload
 *
 * @returns {object[]}
 */
const fetchBoards = async ({ commit, getters }, payload) => {
  const response = await ApiService.fetchBoards(payload.projectId)
  const boards = response.data

  boards.forEach(board => {
    saveBoard({ commit, getters }, board)
  })

  return boards
}

/**
 * @param {Store}         context
 * @param {Store.commit}  context.commit
 * @param {Store.state}   context.state
 * @param {Store.getters} context.getters
 * @param {object}        payload
 */
const fetchBoard = async ({ commit, state, getters }, payload) => {
  const response = await ApiService.fetchBoard(
    payload.projectId,
    payload.boardId
  )
  const board = response.data

  saveBoard({ commit, getters }, board)

  const styles = board.styles.map(style => {
    return {
      projectId: board.project_id,
      ...style
    }
  })

  styles.forEach(style => {
    saveStyle({ commit, getters }, style)
  })
  const options = styles.map(style => style.options).flat()

  options
    .map(option => {
      return {
        projectId: board.project_id,
        ...option
      }
    })
    .forEach(option => {
      saveOption({ commit, getters }, option)
    })

  const details = styles.map(style => style.details).flat()

  details.forEach(detail => {
    saveDetail({ commit, getters }, detail)
  })

  return board
}

/**
 * @param   {Store}        context
 * @param   {Store.commit} context.commit
 * @param   {object}       payload
 *
 * @returns {object}
 */
const addBoard = async ({ commit }, payload) => {
  const response = await ApiService.createBoard(
    payload.projectId,
    payload.templateType,
    payload.boardName,
    payload.index
  )

  const board = response.data

  commit('addBoard', board)

  return board
}

/**
 * @param {Store}         context
 * @param {Store.commit}  context.commit
 * @param {Store.getters} context.getters
 * @param {object}        payload
 */
const deleteBoard = async ({ commit, getters }, payload) => {
  await ApiService.deleteBoard(payload.projectId, payload.boardId)
  commit('deleteBoard', payload.boardId)

  getters
    .getStylesByBoardId(payload.boardId)
    .forEach(style => commit('deleteStyle', style.id))
}

/**
 * @param   {Store}        context
 * @param   {Store.commit} context.commit
 * @param   {object}       payload
 *
 * @returns {object}
 */
const updateBoardIndex = async ({ commit }, payload) => {
  const response = await ApiService.updateBoardIndex(
    payload.projectId,
    payload.boardId,
    payload.index
  )

  const board = response.data

  commit('updateBoard', board)

  return board
}

/**
 * @param   {Store}        context
 * @param   {Store.commit} context.commit
 * @param   {object}       payload
 *
 * @returns {object}
 */
const updateBoardName = async ({ commit }, payload) => {
  const response = await ApiService.updateBoardName(
    payload.projectId,
    payload.boardId,
    payload.boardName
  )

  const board = response.data

  commit('updateBoard', board)

  return board
}

/**
 * @param   {Store}        context
 * @param   {Store.commit} context.commit
 * @param   {object}       payload
 *
 * @returns {object}
 */
const updateBoardLabel = async ({ commit }, payload) => {
  const response = await ApiService.updateBoardLabel(
    payload.projectId,
    payload.boardId,
    payload.boardLabel
  )

  const board = response.data

  commit('updateBoard', board)

  return board
}

/**
 * @param   {Store}         context
 * @param   {Store.commit}  context.commit
 * @param   {Store.getters} context.getters
 * @param   {object}        payload
 *
 * @returns {object}
 */
const updateBoardPreviewOptionStoreOnly = ({ commit, getters }, payload) => {
  const board = getters.getBoardById(payload.boardId)

  commit('updateBoard', { ...board, preview_option: payload.previewOption })

  return board
}

/**
 * @param   {Store}         context
 * @param   {Store.commit}  context.commit
 * @param   {Store.getters} context.getters
 * @param   {object}        payload
 *
 * @returns {object}
 */
const updateBoardIndexStoreOnly = ({ commit, getters }, payload) => {
  const board = getters.getBoardById(payload.boardId)

  commit('updateBoard', { ...board, index: payload.index })

  return board
}

/**
 * @type {object}
 */
const actions = {
  fetchBoards,
  fetchBoard,
  addBoard,
  deleteBoard,
  updateBoardIndex,
  updateBoardName,
  updateBoardLabel,
  updateBoardPreviewOptionStoreOnly,
  updateBoardIndexStoreOnly
}

export default actions
